/**
 * InitiativePicker — Combobox picker for selecting a parent initiative.
 *
 * Uses EntityPickerBase for the standard autocomplete/combobox pattern.
 * Filters out a specific initiative (and its descendants) to prevent circular references.
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Target } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { EntityPickerBase, BidiText } from '../../../shared/components/common';
import { getEntityUrl } from '../../../shared/utils/entityUrls';
import { filterAndSortBySearch } from '../../../shared/utils/searchScore';
import { useInitiatives } from '../hooks/useInitiativeQueries';
import { InitiativeQuickCreateDialog } from './InitiativeQuickCreateDialog';
import type { SfInitiative } from '../types';

interface InitiativePickerProps {
  /** Currently selected initiative ID (null for none) */
  value: string | null;
  /** Called when an initiative is selected or cleared */
  onChange: (initiativeId: string | null) => void;
  /** Label above the picker */
  label?: string;
  /** Disable the picker */
  disabled?: boolean;
  /** Initiative ID to exclude from the list (to prevent self-reference / circular) */
  excludeId?: string;
  /** Additional specific ids to exclude (e.g. already-linked dependency targets) */
  excludeIds?: string[];
  /** Placeholder shown when nothing is selected. Defaults to the "no parent" label. */
  placeholder?: string;
  /** Additional CSS classes */
  className?: string;
  /** Show selected initiative as a clickable link to its detail page */
  linkToEntity?: boolean;
  /**
   * Allow creating a new initiative inline. Shows an "Add new" action that opens
   * the InitiativeQuickCreateDialog (pre-filled with the typed search) and selects
   * the result without navigating away. Default false (filter/parent contexts).
   */
  allowCreate?: boolean;
  /** Default module for a newly-created initiative (when allowCreate is on). */
  createDefaultModuleId?: string | null;
}

export function InitiativePicker({
  value,
  onChange,
  label,
  disabled,
  excludeId,
  excludeIds,
  placeholder,
  className,
  linkToEntity = false,
  allowCreate = false,
  createDefaultModuleId,
}: InitiativePickerProps) {
  const { t } = useTranslation(['softwareFactory', 'common']);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [createDialogName, setCreateDialogName] = useState('');

  const { data: initiativesData } = useInitiatives({ limit: 200 });
  const allInitiatives = useMemo(() => {
    const items = initiativesData?.items ?? [];
    // Exclude any explicitly-listed ids (e.g. already-linked dependency targets),
    // plus the picker's own id and all of its descendants to prevent circular
    // parent references.
    const excluded = new Set<string>(excludeIds ?? []);
    if (excludeId) {
      excluded.add(excludeId);
      let added = true;
      while (added) {
        added = false;
        for (const item of items) {
          if (item.parent_id && excluded.has(item.parent_id) && !excluded.has(item.id)) {
            excluded.add(item.id);
            added = true;
          }
        }
      }
    }
    if (excluded.size === 0) return items;
    return items.filter((i) => !excluded.has(i.id));
  }, [initiativesData?.items, excludeId, excludeIds]);

  const filteredInitiatives = useMemo(
    () => filterAndSortBySearch(allInitiatives, search, (i) => i.name),
    [allInitiatives, search],
  );

  const addNewLabel = search.trim()
    ? t('softwareFactory:initiatives.createWithName', { name: search.trim() })
    : t('softwareFactory:initiatives.addNew');

  return (
    <>
      <EntityPickerBase<SfInitiative>
        value={value || ''}
        items={filteredInitiatives}
        getItemLabel={(i) => i.name}
        renderItem={(i, isSelected) => (
          <div className="flex items-center gap-2 w-full">
            <Check className={cn('h-4 w-4 shrink-0', isSelected ? 'opacity-100' : 'opacity-0')} />
            <Target className="h-4 w-4 shrink-0 text-muted-foreground" />
            <BidiText className="truncate">{i.name}</BidiText>
          </div>
        )}
        label={label}
        placeholder={placeholder ?? t('softwareFactory:initiatives.noParent')}
        searchPlaceholder={t('softwareFactory:initiatives.searchPlaceholder')}
        emptyMessage={t('softwareFactory:initiatives.emptyTitle')}
        disabled={disabled}
        className={className}
        search={search}
        onSearchChange={setSearch}
        open={open}
        onOpenChange={(isOpen) => {
          setOpen(isOpen);
          if (!isOpen) setSearch('');
        }}
        onSelect={(id) => {
          onChange(id);
          setOpen(false);
        }}
        allowClear
        onClear={() => {
          onChange(null);
          setOpen(false);
        }}
        addNewLabel={addNewLabel}
        onAddNew={allowCreate ? () => {
          setCreateDialogName(search.trim());
          setOpen(false);
          setSearch('');
          setShowCreateDialog(true);
        } : undefined}
        selectedEntityUrl={linkToEntity && value
          ? getEntityUrl({ entityType: 'sf_initiatives', entityId: value })
          : undefined}
        entityIcon={linkToEntity ? Target : undefined}
      />
      {allowCreate && (
        <InitiativeQuickCreateDialog
          open={showCreateDialog}
          onClose={() => setShowCreateDialog(false)}
          navigateOnCreate={false}
          defaultName={createDialogName}
          defaultModuleId={createDefaultModuleId}
          onCreated={(initiative) => {
            onChange(initiative.id);
            setShowCreateDialog(false);
          }}
        />
      )}
    </>
  );
}
